import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2, ClipboardList, ArrowRight, FileCheck2, EyeOff, Loader2,
  Link2Off, ScanSearch, FileWarning,
} from "lucide-react";
import { api } from "@/lib/apiClient";

const KIND_META = {
  incident_no_evidence: { Icon: FileWarning },
  evidence_not_analyzed: { Icon: ScanSearch },
  evidence_unlinked: { Icon: Link2Off },
};

export default function ReviewItems({ matterId, onJump, refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    if (!matterId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/matters/${matterId}/review-items`);
      setItems(data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const act = async (item, action) => {
    setBusy((b) => ({ ...b, [item.key]: true }));
    try {
      await api.post(`/review-items/${encodeURIComponent(item.key)}/action`, { matter_id: matterId, action });
      setItems((prev) => prev.filter((x) => x.key !== item.key));
      toast.message(action === "leave" ? "Marked as documented — it won't show again" : "Reminder dismissed");
    } catch { toast.error("Could not update this item"); }
    finally { setBusy((b) => ({ ...b, [item.key]: false })); }
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-6 flex justify-center" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <Loader2 className="h-5 w-5 animate-spin" style={{ color: "var(--ek-primary)" }} />
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-5 sm:p-6" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="review-items">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
        <h2 className="font-head font-semibold tracking-tight">Items to Review</h2>
        {items.length > 0 && <span className="ek-stamp ml-auto" style={{ color: "var(--ek-warning)", borderColor: "rgba(245,158,11,0.4)" }}>{items.length}</span>}
      </div>

      {items.length === 0 ? (
        <div className="flex items-center gap-2 text-sm mt-3" style={{ color: "var(--ek-secure)" }} data-testid="review-items-clear">
          <CheckCircle2 className="h-4 w-4" /> Everything's in order — nothing to review.
        </div>
      ) : (
        <>
          <p className="text-xs mb-4 mt-1" style={{ color: "var(--ek-text-muted)" }}>
            Fix an item, mark it as intentional, or dismiss the reminder. Nothing here changes your records automatically.
          </p>
          <div className="flex flex-col gap-2.5" data-testid="review-items-list">
            {items.map((item) => {
              const Icon = (KIND_META[item.kind] || {}).Icon || FileWarning;
              const b = busy[item.key];
              return (
                <div key={item.key} className="border rounded-md p-3" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)" }} data-testid={`review-item-${item.key}`}>
                  <div className="flex items-start gap-2.5">
                    <span className="grid place-items-center h-6 w-6 rounded-full shrink-0 mt-0.5" style={{ background: "rgba(245,158,11,0.12)" }}>
                      <Icon className="h-3.5 w-3.5" style={{ color: "var(--ek-warning)" }} />
                    </span>
                    <span className="text-sm flex-1 min-w-0" data-private style={{ color: "var(--ek-text)" }}>{item.label}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3 pl-8">
                    <button
                      onClick={() => onJump && onJump(item.tab, item.target_id, item.target_type)}
                      disabled={b}
                      data-testid={`review-correct-${item.key}`}
                      className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors hover:border-[var(--ek-primary)]"
                      style={{ borderColor: "rgba(46,130,246,0.4)", background: "transparent", color: "var(--ek-primary)" }}
                    >
                      Correct <ArrowRight className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => act(item, "leave")}
                      disabled={b}
                      data-testid={`review-leave-${item.key}`}
                      className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors"
                      style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-secure)" }}
                    >
                      <FileCheck2 className="h-3 w-3" /> Leave As Documented
                    </button>
                    <button
                      onClick={() => act(item, "dismiss")}
                      disabled={b}
                      data-testid={`review-dismiss-${item.key}`}
                      className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs transition-colors"
                      style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-text-muted)" }}
                    >
                      <EyeOff className="h-3 w-3" /> Dismiss
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
